/**
 * Slim floating island navbar (EL-060 brief): inset from the viewport edge,
 * not full-bleed, not an overlay -- it never covers evidence content, on
 * scroll or otherwise. Exactly the three routes architecture section 12
 * names, plus the theme toggle, plus a case switcher now that there are
 * two known cases.
 */
import { NavLink } from "react-router-dom";

import { KNOWN_CASES } from "../../lib/cases";
import { ThemeToggle } from "./ThemeToggle";

const LINKS = [
  { to: (caseId: string) => `/cases/${caseId}`, label: "Case overview" },
  { to: (caseId: string) => `/cases/${caseId}/replay`, label: "Differential replay" },
  { to: (caseId: string) => `/cases/${caseId}/receipt`, label: "Fix receipt" },
] as const;

function linkClassName({ isActive }: { isActive: boolean }): string {
  return [
    "rounded-full px-3 py-1.5 text-sm transition-colors",
    isActive ? "bg-accent text-accent-ink" : "text-ink-muted hover:text-ink",
  ].join(" ");
}

function caseSwitcherClassName({ isActive }: { isActive: boolean }): string {
  return [
    "rounded-full px-3 py-1.5 text-xs transition-colors",
    isActive ? "bg-accent text-accent-ink" : "text-ink-muted hover:text-ink",
  ].join(" ");
}

export function Nav({ caseId }: { caseId?: string }) {
  return (
    <header className="sticky top-4 z-10 mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-1 rounded-full border border-rule bg-canvas-raised px-2 py-2 shadow-[0_1px_0_0_var(--color-rule)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:relative focus:rounded-full focus:bg-accent focus:px-3 focus:py-1.5 focus:text-sm focus:text-accent-ink"
      >
        Skip to content
      </a>
      <span className="font-editorial inline-flex items-center gap-1.5 px-3 text-sm text-ink">
        <img src="/favicon.png" alt="" width={20} height={20} className="h-5 w-5 shrink-0" />
        ExploitLens
      </span>
      {/* Which case's evidence is currently shown -- switching remounts the
          bundle provider for the new caseId (see App.tsx's CaseRoute), it
          never reuses the previous case's already-loaded state. */}
      <nav aria-label="Case selection" className="flex items-center gap-1">
        {KNOWN_CASES.map((entry) => (
          <NavLink
            key={entry.caseId}
            to={`/cases/${entry.caseId}`}
            className={caseSwitcherClassName}
          >
            {entry.label}
          </NavLink>
        ))}
      </nav>
      <span className="mx-1 h-5 w-px bg-rule" aria-hidden="true" />
      <nav aria-label="Case navigation" className="flex items-center gap-1">
        {LINKS.map((link) => (
          <NavLink
            key={link.label}
            to={caseId ? link.to(caseId) : "#"}
            className={linkClassName}
            aria-disabled={caseId === undefined}
            end
          >
            {link.label}
          </NavLink>
        ))}
      </nav>
      <span className="mx-1 h-5 w-px bg-rule" aria-hidden="true" />
      <ThemeToggle />
    </header>
  );
}
